import React from 'react'
import { calculateTaskDays } from '../../../components/Tasks/taskUtil'
import useMassUpdateTasks from '../../../api/aws/tasks/useMassUpdateTasks'
import { useCreateTimeline } from '../../../api/aws/useCreateTimeline'
import { Task } from '../../../types'
import { handleFormatTypesString } from '../../../components/Forms/types'
import { formatDateString, workday } from '../../../components/Tasks/taskUtil'
import { createTimelineEntriesForChanges } from '../../../components/Timeline/timelineTypes'
import { useHandleTaskUpdate } from '../../../components/MTB/useHandleTaskUpdate'
import { useSnackbarContext } from 'context/SnackbarProvider/SnackbarProvider'
import { useUpdateMassItems } from 'api/aws/useUpdateMassItems'
import { useUpdateOrganization } from 'api/aws/useEntityUpdate'
import { GridRowSelectionModel } from '@mui/x-data-grid-premium'
import { useEntitiesFromSearch } from 'api/search'

interface _UseProcessTaskRowUpdateArgs {
  selectedMassData: Task[]
  setSelectedMassData: React.Dispatch<React.SetStateAction<Task[]>>
}

export const useProcessTaskRowUpdate = () => {
  const currentUser = JSON.parse(localStorage.getItem('currentUser') || '{}')
  const { showSnackbar: _showSnackbar } = useSnackbarContext()
  const { allTasks } = useEntitiesFromSearch()
  const { handleMassUpdate, isUpdating } = useMassUpdateTasks('teamtasks')
  const { mutate: postTimelineEntry } = useCreateTimeline()
  const { handleTaskUpdate } = useHandleTaskUpdate()
  const { mutate: updateMassItems } = useUpdateMassItems()
  const { mutate: updateOrganization } = useUpdateOrganization()

  const handleHighPrioChange = (taskId: string, isChecked: boolean) => {
    console.log('taskId', taskId)
    // Find the task by its SK.
    const task = allTasks.find((t: Task) => t.SK === taskId)
    if (!task) {
      console.warn('[handleHighPrioChange] No valid task found for update.')
      return
    }
    // Trigger the single update.
    handleTaskUpdate([{ PK: task.PK, SK: task.SK }], { highPrio: isChecked })
  }

  const handlePostdatedChange = (taskId: string, isChecked: boolean) => {
    console.log('handlePostdatedChange taskId', taskId, 'isChecked', isChecked)
    // Find the task by its SK.
    const task = allTasks.find((t: Task) => t.SK === taskId)
    if (!task) {
      console.warn('[handlePostdatedChange] No valid task found for update.')
      return
    }
    // Trigger the single update.
    handleTaskUpdate([{ PK: task.PK, SK: task.SK }], { isPostdated: isChecked })
  }
  const processTaskRowUpdate = async (updatedRow: Task, oldRow: Task, rowSelectionModel: GridRowSelectionModel) => {
    console.log('updatedRow', updatedRow, 'oldRow', oldRow, 'rowSelectionModel', rowSelectionModel)
    const updatedData: Partial<Task> = {}
    /////////////// UPDATE SLA DAYS //////////////////////////
    // Determine if we need to update SLA-related fields
    const typeChanged = updatedRow.type !== oldRow.type
    const startDateChanged = updatedRow.startDate !== oldRow.startDate

    // Check if taskDays was missing or 0, even if type or start date didn’t change
    const needsTaskDays =
      // updatedRow.taskDays === 0 || updatedRow.taskDays === undefined ||
      startDateChanged || typeChanged

    if (needsTaskDays) {
      const taskType = handleFormatTypesString(updatedRow.type)
      const startDate = updatedRow.startDate

      const newTaskDays = calculateTaskDays(taskType)
      const newDueDate = workday(startDate, newTaskDays)

      // Patch taskDays directly for row rendering
      updatedRow.taskDays = newTaskDays
      updatedData.taskDays = newTaskDays

      if (newDueDate) {
        updatedRow.dateDue = formatDateString(newDueDate)
        updatedRow.endDate = newDueDate.toISOString()
        updatedData.dateDue = updatedRow.dateDue
        updatedData.endDate = updatedRow.endDate
      }
      // 📌 Send timeline entry
      console.log('[✅ SLA Recalculation]', {
        type: taskType,
        taskDays: newTaskDays,
        startDate,
        dateDue: updatedRow.dateDue,
        endDate: updatedRow.endDate,
      })
    }

    ///////////////////////////////////////////////////////////
    if (updatedRow.name !== oldRow.name) updatedData.name = updatedRow.name
    if (updatedRow.masterStatus !== oldRow.masterStatus) updatedData.masterStatus = updatedRow.masterStatus
    if (updatedRow.status && updatedRow.status !== oldRow.status) updatedData.status = updatedRow.status
    if (updatedRow.startDate !== oldRow.startDate) updatedData.startDate = updatedRow.startDate
    // if (updatedRow.dateDue !== oldRow.dateDue) updatedData.dateDue = updatedRow.dateDue
    if (updatedRow.endDate !== oldRow.endDate) updatedData.endDate = updatedRow.endDate
    if (
      updatedRow.isMarketing !== oldRow.isMarketing ||
      updatedRow.isCreative !== oldRow.isCreative ||
      updatedRow.isWebsite !== oldRow.isWebsite
    ) {
      updatedData.isMarketing = updatedRow.isMarketing
      updatedData.isCreative = updatedRow.isCreative
      updatedData.isWebsite = updatedRow.isWebsite
    }
    if (Object.keys(updatedData).length > 0) {
      console.log('updatedData', updatedData)
      if (currentUser) {
        const changedFields = Object.keys(updatedData)
        const timelineEntries = createTimelineEntriesForChanges(updatedRow, oldRow, changedFields, currentUser)
        timelineEntries.forEach(entry => postTimelineEntry(entry))
      }
      // If multiple rows are selected, trigger mass update.
      if (rowSelectionModel && Array.from(rowSelectionModel?.ids)?.length > 1) {
        handleMassUpdate(updatedData, rowSelectionModel)

        return { ...updatedRow, ...updatedData }
      } else {
        // Single row update logic
        if (updatedRow?.isParent) {
          try {
            await handleTaskUpdate([{ PK: updatedRow.PK, SK: updatedRow.SK }], updatedData)
          } catch (error) {
            console.error('Error updating row:', error)
            throw error
          }
        }

        return { ...updatedRow, ...updatedData }
      }
    }

    return updatedRow
  }

  return { processTaskRowUpdate, handleHighPrioChange, handlePostdatedChange }
}
